<template>
  <v-card
    v-if="show"
    class="mt-2 mb-2"
    outlined
  >
    <v-card-title
      v-if="!shouldShowConfirmation"
      class="d-flex align-start px-2 py-2"
      style="position: relative;"
    >
      <span class="text-h6 px-3 font-weight-medium pr-10">
        That wasn't helpful — my bad. Help me fix it.
      </span>
      <v-btn
        icon
        x-small
        style="position: absolute; right: 12px; top: 12px;"
        @click.stop="closeCard"
      >
        <v-icon small>
          mdi-close
        </v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="px-6 pb-2 pt-2">
      <div v-if="!shouldShowConfirmation">
        <div class="pb-4">
          What should I change?
        </div>

        <div
          class="text-subtitle-2 mb-2"
          style="font-weight: 400;"
        >
          Quick Reasons
        </div>

        <div
          class="d-flex flex-wrap quick-reasons mb-4"
          style="gap: 12px;"
        >
          <v-btn
            v-for="reason in quickReasons"
            :key="reason"
            small
            outlined
            :color="selectedReasons.includes(reason) ? 'primary' : ''"
            :class="{ 'primary--text': selectedReasons.includes(reason) }"
            @click="addReason(reason)"
          >
            {{ reason }}
          </v-btn>
        </div>

        <v-textarea
          v-model="feedbackText"
          outlined
          dense
          class="mt-3"
          label="Tell us more..."
          rows="3"
          placeholder="Select quick reasons above or type your own feedback"
        />
        <div class="d-flex justify-end">
          <v-btn

            :color="COLORS.grainfoxAIButton"
            class="white--text"
            small
            :disabled="!canSubmit"
            @click="submitFeedback"
          >
            REPORT
          </v-btn>
        </div>
      </div>

      <div
        v-else
        class="text-center pt-0"
      >
        <div class="text-body-2">
          Thanks for flagging this. I saved your feedback.
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { COLORS } from '@/helpers/colors';

export default {
  name: 'FeedbackDialog',

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    interactionId: {
      type: Number,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      quickReasons: [
        'Numbers look wrong',
        'Answer is too broad',
        'Region/crop mismatch',
        'Missed my question',
        'Needs sources/charts',
        'Not compliant',
      ],
      COLORS,
      selectedReasons: [],
      feedbackText: '',
      localShowConfirmation: false,
    };
  },

  computed: {
    ...mapGetters('chatbot', ['getFeedbackForInteraction']),

    canSubmit() {
      return this.feedbackText.trim().length > 0;
    },

    hasSubmittedFeedback() {
      if (!this.interactionId) return false;
      const feedback = this.getFeedbackForInteraction(this.interactionId);
      return feedback && feedback.value === -1;
    },

    shouldShowConfirmation() {
      return this.localShowConfirmation || this.hasSubmittedFeedback;
    },
  },

  watch: {
    show(val) {
      if (val) {
        this.selectedReasons = [];
        this.feedbackText = '';
        this.localShowConfirmation = false;

        if (this.hasSubmittedFeedback) {
          this.localShowConfirmation = true;
        }
      }
    },

    hasSubmittedFeedback: {
      handler(newVal) {
        if (newVal) {
          this.localShowConfirmation = true;
        }
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions('chatbot', ['upsertFeedback']),

    addReason(reason) {
      if (this.selectedReasons.includes(reason)) {
        this.selectedReasons = this.selectedReasons.filter((r) => r !== reason);
      } else {
        this.selectedReasons.push(reason);
      }
      this.updateFeedbackText();
    },

    updateFeedbackText() {
      const textParts = [...this.selectedReasons];

      const currentText = this.feedbackText.trim();
      if (currentText) {
        const currentParts = currentText.split(',')
          .map((part) => part.trim())
          .filter((part) => part.length > 0);

        currentParts.forEach((part) => {
          const isQuickReason = this.quickReasons.includes(part);
          const alreadyInTextParts = textParts.includes(part);

          if (!isQuickReason && !alreadyInTextParts) {
            textParts.push(part);
          }
        });
      }

      this.feedbackText = textParts.join(', ');
    },

    async submitFeedback() {
      if (!this.canSubmit || !this.interactionId) return;

      try {
        await this.upsertFeedback({
          interactionId: this.interactionId,
          value: -1,
          reason: this.feedbackText.trim(),
        });

        this.localShowConfirmation = true;
      } catch (error) {
        this.$snackbar?.error('Error submitting detailed feedback');
      }
    },

    closeCard() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/colors/colors";

::v-deep(.v-btn--outlined) {
  border-width: 1px;
}

::v-deep(.v-btn) {
  text-transform: none;
}

.v-btn.v-btn--outlined.primary--text {
  background-color: rgba(var(--v-primary-base), 0.08) !important;
}
::v-deep .v-text-field textarea {
  font-size: 14px;
}
.quick-reasons ::v-deep .v-btn {
  font-weight: 400;
}
::v-deep .v-text-field--outlined:not(.v-input--is-focused) fieldset {
  border-color: colors.$primaryGrey !important;
  border-width: 1px !important;
}
</style>
